import React, { startTransition } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { LogOut } from "lucide-react";

import useSideNav from "../services/useSideNav";
import ConfirmLogoutModal from "../utils/ConfirmLogoutModal";
import logoImage from "../images/mini-logo.svg";

const getLoggedInUser = () => {
  try {
    const storedUser = sessionStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User session parse error:", error);
    return null;
  }
};

const normalizePath = (path = "") => {
  const normalizedPath = String(path)
    .trim()
    .toLowerCase()
    .split("?")[0]
    .split("#")[0];

  if (
    normalizedPath.length > 1 &&
    normalizedPath.endsWith("/")
  ) {
    return normalizedPath.slice(0, -1);
  }

  return normalizedPath;
};

const isPathActive = (currentPath, targetPath) => {
  const current = normalizePath(currentPath);
  const target = normalizePath(targetPath);

  if (!current || !target) return false;

  return (
    current === target ||
    current.startsWith(`${target}/`)
  );
};

const getItemUrl = (item = {}, type = "service") => {
  if (type === "module") {
    return normalizePath(
      item?.module_url ||
        item?.url ||
        item?.route ||
        "",
    );
  }

  return normalizePath(
    item?.service_url ||
      item?.url ||
      item?.route ||
      "",
  );
};

const findBestActiveItem = (
  items = [],
  currentPath = "",
  type = "service",
) => {
  if (!Array.isArray(items)) return null;

  const current = normalizePath(currentPath);
  const matchedItems = [];

  const collectMatches = (list = []) => {
    list.forEach((item) => {
      const itemUrl = getItemUrl(item, type);

      if (
        itemUrl &&
        (current === itemUrl ||
          current.startsWith(`${itemUrl}/`))
      ) {
        matchedItems.push(item);
      }

      const children =
        item?.services ||
        item?.sub_services ||
        item?.submenu ||
        item?.children ||
        [];

      if (Array.isArray(children)) {
        collectMatches(children);
      }
    });
  };

  collectMatches(items);

  if (matchedItems.length === 0) {
    return null;
  }

  return [...matchedItems].sort((a, b) => {
    const aUrl = getItemUrl(a, type);
    const bUrl = getItemUrl(b, type);

    return bUrl.length - aUrl.length;
  })[0];
};

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const userData = getLoggedInUser();

  const userID =
    userData?.userid ||
    userData?.userId ||
    userData?.userID ||
    "";

  const {
    modules = [],
    loading,
    error,
    refreshSidebar,
  } = useSideNav(userID);

  const currentPath = normalizePath(location.pathname);

  const hasActiveServicePath = (services = [], currentPath = "") => {
  if (!Array.isArray(services)) return false;

  return services.some((service) => {
    const serviceUrl =
      service?.service_url ||
      service?.url ||
      service?.route ||
      "";

    const currentServiceActive = isPathActive(
      currentPath,
      serviceUrl,
    );

    const childServices =
      service?.services ||
      service?.sub_services ||
      service?.submenu ||
      service?.children ||
      [];

    const childServiceActive = hasActiveServicePath(
      childServices,
      currentPath,
    );

    return currentServiceActive || childServiceActive;
  });
};

const activeModules = modules.filter((module) => {
  const isModuleActive = isPathActive(
    currentPath,
    module?.module_url,
  );

  const isServiceActive = hasActiveServicePath(
    module?.services,
    currentPath,
  );

  return isModuleActive || isServiceActive;
});

const activeModule =
  activeModules.sort((a, b) => {
    const aModulePath = normalizePath(
      a?.module_url,
    );

    const bModulePath = normalizePath(
      b?.module_url,
    );

    const aServiceMatch = findBestActiveItem(
      a?.services,
      currentPath,
      "service",
    );

    const bServiceMatch = findBestActiveItem(
      b?.services,
      currentPath,
      "service",
    );

    const aMatchedPath =
      getItemUrl(aServiceMatch, "service") ||
      aModulePath;

    const bMatchedPath =
      getItemUrl(bServiceMatch, "service") ||
      bModulePath;

    return bMatchedPath.length - aMatchedPath.length;
  })[0] || null;

  const activeService = activeModule
  ? findBestActiveItem(
      activeModule?.services,
      currentPath,
      "service",
    )
  : null;

const handleLogout = () => {
  
  sessionStorage.setItem("isLoggingOut", "true");

  sessionStorage.removeItem("sessionID");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("entitlement");

  localStorage.removeItem("sessionID");
  localStorage.removeItem("user");
  localStorage.removeItem("entitlement");

  sessionStorage.clear();
  localStorage.clear();

  navigate("/", {
    replace: true,
  });
};

  const handleNavigate = (path) => {
    if (!path) return;

    const targetPath = normalizePath(path);

    if (currentPath === targetPath) return;

    startTransition(() => {
      navigate(path);
    });
  };

  return (
    <div className="flex h-screen montserrat-font-2">
      {/* LEFT MODULE SIDEBAR */}
      <div className="w-[65px] bg-white border-r flex flex-col h-screen shrink-0">
        <div className="flex flex-col flex-1 overflow-hidden">
          <NavLink
            to="/dashboard"
            className="flex flex-col items-center py-4"
          >
            <img
              src={logoImage}
              alt="Logo"
              className="h-14 mb-2 mt-5 cursor-pointer"
            />
          </NavLink>

          <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col">
            {loading && modules.length === 0 && (
              <div className="flex justify-center py-4">
                <div className="w-5 h-5 rounded-full border-2 border-gray-200 border-t-[#00A884] animate-spin" />
              </div>
            )}

            {!loading &&
              error &&
              modules.length === 0 && (
                <div className="px-1 py-3 text-center">
                  <p className="text-[9px] text-red-500 break-words">
                    {error}
                  </p>

                  <button
                    type="button"
                    onClick={refreshSidebar}
                    className="mt-2 text-[9px] text-[#00A884] underline"
                  >
                    Retry
                  </button>
                </div>
              )}

            {!loading &&
              !error &&
              modules.length === 0 && (
                <p className="px-1 py-3 text-center text-[9px] text-gray-500">
                  No modules
                </p>
              )}

            {modules
              .filter(
                (module) =>
                  module &&
                  module.module_url,
              )
              .map((module, index) => {
                const isActive =
                  activeModule === module;

                const moduleKey =
                  module?.id ||
                  module?.module_id ||
                  module?.pk_module_id ||
                  `${module.module_name}-${index}`;

                return (
                  <button
                    key={moduleKey}
                    type="button"
                    onClick={() =>
                      handleNavigate(
                        module.module_url,
                      )
                    }
                    className={`relative flex flex-col items-center py-2 w-full transition-colors ${
                      isActive
                        ? "bg-[#01C37D] text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {(module.module_selected_icon ||
                      module.module_active_icon) && (
                      <img
                        src={
                          isActive
                            ? module.module_selected_icon ||
                              module.module_active_icon
                            : module.module_active_icon ||
                              module.module_selected_icon
                        }
                        className="w-5 h-5 mb-1 object-contain"
                        alt={
                          module.module_name ||
                          "Module"
                        }
                      />
                    )}

                    <span className="text-[10px] text-center max-w-[60px] truncate">
                      {module.module_name}
                    </span>
                  </button>
                );
              })}
          </div>
        </div>

        <div className="flex justify-center border-t py-3">
          <ConfirmLogoutModal
            onConfirm={handleLogout}
            trigger={
              <button
                type="button"
                className="flex flex-col items-center text-[#00A884] hover:bg-gray-100 w-full py-3"
              >
                <LogOut
                  className="w-5 h-5 mb-1 opacity-80 hover:opacity-100 hover:text-red-500 transition-colors cursor-pointer"
                  strokeWidth={2}
                />

                <span className="text-[10px]">
                  Logout
                </span>
              </button>
            }
          />
        </div>
      </div>

      {/* INTERNAL SERVICE SIDEBAR */}
      {activeModule &&
        String(
          activeModule?.module_name || "",
        ).toLowerCase() !== "dashboard" && (
          <div className="w-[75px] bg-white border-r flex flex-col h-screen shrink-0">
            <div className="h-[70px] flex items-center justify-center border-b-2 border-gray-300 mb-2 bg-white shrink-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-2xl text-gray-600 hover:text-black"
              >
                ←
              </button>
            </div>

            <div className="flex flex-col text-xs flex-1 overflow-y-auto hide-scrollbar">
        {Array.isArray(activeModule?.services) &&
  activeModule.services
    .filter(
      (service) =>
        service &&
        service.service_url,
    )
    .map((service, index) => {
      const isActive =
        activeService === service;

      const serviceKey =
        service?.id ||
        service?.service_id ||
        service?.pk_service_id ||
        `${service.service_name}-${index}`;

      return (
        <button
          key={serviceKey}
          type="button"
          onClick={() =>
            handleNavigate(
              service.service_url,
            )
          }
          className={`relative flex flex-col items-center justify-center py-2 w-full transition-colors ${
            isActive
              ? "bg-[#D1F1E5] text-gray-700 font-medium"
              : "text-gray-700 hover:bg-gray-50 cursor-pointer"
          }`}
        >
          {isActive && (
            <div className="absolute right-0 top-0 h-full w-[3px] bg-[#00A884]" />
          )}

          {(service.service_active_icon ||
            service.service_icon) && (
            <img
              src={
                isActive
                  ? service.service_active_icon ||
                    service.service_icon
                  : service.service_icon ||
                    service.service_active_icon
              }
              width={20}
              height={20}
              alt={
                service.service_name ||
                "Service"
              }
              className="w-5 h-5 mb-2 object-contain"
            />
          )}

          <span className="text-center leading-tight text-[10px] max-w-[68px] truncate">
            {service.service_name}
          </span>
        </button>
      );
    })}
            </div>
          </div>
        )}
    </div>
  );
};

export default NavigationBar;

